import { userPlanAtom } from 'app/atoms';
import { LogoRenderer } from 'app/design/branding/logo';
import { LogoOneLine } from 'app/design/icons/logo-one-line';
import { Column } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { IconArrowRight } from '@tabler/icons-react-native';
import { Button } from 'app/design/primitives/button';
import { useScopedTranslation } from 'app/hooks/i18n';
import { env } from 'app/lib/env';
import { useProfile } from 'app/network/profile';
import { useUser, useUserCurrency } from 'app/network/user';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useAtomValue } from 'jotai';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { Extrapolation, interpolate, useAnimatedStyle, withSpring, type SharedValue } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StepName } from './present-community-shared.constants';

export { StepName } from './present-community-shared.constants';

interface CongratulationComponentProps {
  onPress?: () => void;
}

export function useAnimationStyle(
  itemWidth: number,
  index: number,
  scrollX: SharedValue<number>,
  fromOpacity = 0,
  toOpacity = 1,
  fromX?: number,
  toX?: number,
  damping = 90,
  stiffness = 100,
) {
  return useAnimatedStyle(() => {
    const inputRange = [(index - 1) * itemWidth, index * itemWidth, (index + 1) * itemWidth];
    const opacity = interpolate(scrollX.value, inputRange, [fromOpacity, toOpacity, fromOpacity], Extrapolation.CLAMP);

    let translationTransform = undefined;
    if (fromX !== undefined && toX !== undefined) {
      const translateX = interpolate(scrollX.value, inputRange, [fromX, toX, -fromX], Extrapolation.CLAMP);
      translationTransform = [
        {
          translateX: withSpring(translateX, {
            damping,
            stiffness,
          }),
        },
      ];
    }

    return {
      opacity: withSpring(opacity, {
        damping,
        stiffness,
      }),
      transform: translationTransform,
    };
  });
}

function PriceBullet({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <Text
      textAlign="center"
      variant="bodyBase"
      style={{ color: theme.colors['text.primary'], fontSize: 18, lineHeight: 26 }}
    >
      {`• ${label}`}
    </Text>
  );
}

const LOGO_ONE_LINE_WIDTH = 120;
const LOGO_ONE_LINE_ASPECT_RATIO = 1584 / 576;
const LOGO_ONE_LINE_HEIGHT = LOGO_ONE_LINE_WIDTH / LOGO_ONE_LINE_ASPECT_RATIO;

export function CongratulationComponent({ onPress }: CongratulationComponentProps) {
  const theme = useTheme();
  const { t } = useScopedTranslation();
  const { t: tPresentCommunity, i18n } = useTranslation('presentCommunity');
  const user = useUser();
  const userCurrency = useUserCurrency();
  const userProfile = useProfile(user.data?.id);
  const myPlan = useAtomValue(userPlanAtom);
  const suffix = myPlan?.[0]?.code.name === 'freeTrial' ? '.freeTrial' : '';
  const stepName = StepName.Congratulation;
  const foreground = theme.colors['text.primary'];
  const useHorizontalFrenchLogo = i18n.language.startsWith('fr') && !env.IS_PARTAKE;
  const contentContainerStyle = {
    alignItems: 'center' as const,
    gap: theme.spacing['spacing.general.x4'],
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingBottom: theme.spacing['spacing.general.x8'],
  };
  return (
    <SafeAreaView
      style={{
        flex: 1,
        width: '100%',
        backgroundColor: theme.colors['surface.tertiary'],
      }}
    >
      <Page
        paddingStyle={{
          paddingTop: theme.spacing['spacing.general.x4'],
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }}
        accessory={
          <Column
            alignItems="center"
            paddingBottom="spacing.general.x4"
            paddingHorizontal="spacing.general.x4"
            width="100%"
          >
            <Button
              onPress={onPress}
              variant="primary"
              width="100%"
              style={{
                borderRadius: theme.borderRadii['radius.full'],
                paddingVertical: theme.spacing['spacing.general.x3'],
                backgroundColor: foreground,
              }}
            >
              <RowLikeButtonLabel label={t(`${stepName}.buttonText`)} />
            </Button>
          </Column>
        }
      >
        <ScrollView contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
          {useHorizontalFrenchLogo ? (
            <LogoOneLine color={foreground} height={LOGO_ONE_LINE_HEIGHT} width={LOGO_ONE_LINE_WIDTH} />
          ) : (
            <LogoRenderer color={foreground} width={120} />
          )}

          <Text
            textAlign="center"
            variant="headerXSmall"
            style={{ color: foreground, fontWeight: '700', fontSize: 40, lineHeight: 44 }}
          >
            {t(`${stepName}.congratulation`)}
          </Text>

          <Text textAlign="center" variant="bodyBase" style={{ color: foreground, fontSize: 18, lineHeight: 26 }}>
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={`${stepName}${suffix}.text1`}
              ns="presentCommunity"
              values={{ name: userProfile.data?.firstName }}
            />
          </Text>

          <PCImage
            source={userProfile.data?.picture}
            style={{
              width: 140,
              height: 140,
              borderRadius: 999,
              borderColor: 'white',
              borderWidth: 3,
            }}
          />

          {!isEmpty(`${stepName}${suffix}.text2`) ? (
            <Text
              textAlign="center"
              variant="headerXSmall"
              style={{ color: foreground, fontWeight: '700', fontSize: 30, lineHeight: 36 }}
            >
              <Trans
                components={{ strong: <Text fontWeight="700" /> }}
                i18nKey={`${stepName}${suffix}.text2`}
                ns="presentCommunity"
                values={{ name: myPlan?.[0]?.code.name }}
              />
            </Text>
          ) : null}

          <Text textAlign="center" variant="bodyBase" style={{ color: foreground, fontSize: 30, lineHeight: 36 }}>
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={`${stepName}${suffix}.text3`}
              ns="presentCommunity"
              values={{ name: myPlan?.[0]?.code.name }}
            />
          </Text>

          {suffix ? (
            <Column alignItems="center" gap="spacing.general.x4" width="100%">
              <Text textAlign="center" variant="bodyBase" style={{ color: foreground, fontSize: 18, lineHeight: 26 }}>
                {t(`${stepName}${suffix}.text4`)}
              </Text>
              <Column alignItems="center" gap="spacing.general.x1">
                <PriceBullet
                  label={tPresentCommunity(`${stepName}${suffix}.price.monthly.${userCurrency.toUpperCase()}`)}
                />
                <PriceBullet
                  label={tPresentCommunity(`${stepName}${suffix}.price.yearly.${userCurrency.toUpperCase()}`)}
                />
              </Column>
              <Text textAlign="center" variant="bodyBase" style={{ color: foreground, fontSize: 18, lineHeight: 26 }}>
                {t(`${stepName}${suffix}.text5`)}
              </Text>
            </Column>
          ) : (
            <Text textAlign="center" variant="bodyBase" style={{ color: foreground, fontSize: 20, lineHeight: 26 }}>
              <Trans
                components={{ strong: <Text fontWeight="700" /> }}
                i18nKey={`${stepName}.text4`}
                ns="presentCommunity"
                values={{ name: myPlan?.[0]?.code.name }}
              />
            </Text>
          )}
        </ScrollView>
      </Page>
    </SafeAreaView>
  );
}

function RowLikeButtonLabel({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing['spacing.general.x2'],
      }}
    >
      <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>{label}</Text>
      <IconArrowRight strokeWidth={ICON_STROKE_WIDTH} color="white" size={16} />
    </View>
  );
}
